import {
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  Typography,
  styled,
} from "@mui/material"
import { AlertLevel, isAlertLevel } from "@phc/common"
import { AssetNotificationAlertLevel } from "@phc/common/gen/preferencesGen"
import { useCallback } from "react"
import { extraColors } from "../../../../../utils/theme"
import { AlertPill } from "../../../../Shared/AlertPill"
import { useNotificationContext } from "../../contexts/NotificationContext"

const AlertPillRow = styled("div")({
  display: "grid",
  gridAutoFlow: "column",
  gap: 6,
})

const FormControlLabelStyled = styled(FormControlLabel)({
  backgroundColor: extraColors.translucent5,
  borderRadius: 3,
  width: "100%",
})

function convertAlertLevelToAssetNotificationAlertLevel(
  alertLevel: AlertLevel
) {
  switch (alertLevel) {
    case AlertLevel.ADVISORY:
      return AssetNotificationAlertLevel.ASSET_NOTIFICATION_ALERT_LEVEL_ADVISORY
    case AlertLevel.WARNING:
      return AssetNotificationAlertLevel.ASSET_NOTIFICATION_ALERT_LEVEL_WARNING
    case AlertLevel.WATCH:
      return AssetNotificationAlertLevel.ASSET_NOTIFICATION_ALERT_LEVEL_WATCH
    default:
      return AssetNotificationAlertLevel.ASSET_NOTIFICATION_ALERT_LEVEL_UNSPECIFIED
  }
}

function convertAssetNotificationAlertLevelToAlertLevel(
  assetNotificationAlertLevel: AssetNotificationAlertLevel
) {
  switch (assetNotificationAlertLevel) {
    case AssetNotificationAlertLevel.ASSET_NOTIFICATION_ALERT_LEVEL_ADVISORY:
      return AlertLevel.ADVISORY
    case AssetNotificationAlertLevel.ASSET_NOTIFICATION_ALERT_LEVEL_WARNING:
      return AlertLevel.WARNING
    case AssetNotificationAlertLevel.ASSET_NOTIFICATION_ALERT_LEVEL_WATCH:
      return AlertLevel.WATCH
    case AssetNotificationAlertLevel.ASSET_NOTIFICATION_ALERT_LEVEL_UNSPECIFIED:
    default:
      return ""
  }
}

export const AlertLevelRadioGroup: React.FC = () => {
  const { alert_level, setAlertLevel } = useNotificationContext()

  const onChangeAlertLevel = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const alertLvl = event.currentTarget.value
      if (isAlertLevel(alertLvl)) {
        setAlertLevel(convertAlertLevelToAssetNotificationAlertLevel(alertLvl))
      }
    },
    [setAlertLevel]
  )
  return (
    <FormControl sx={{ width: "100%" }}>
      <FormLabel>
        <Typography variant="body2">
          Select the levels at which you’d like to be notified.
        </Typography>
      </FormLabel>
      <RadioGroup
        value={convertAssetNotificationAlertLevelToAlertLevel(alert_level)}
        name="alert levels"
        onChange={onChangeAlertLevel}
        sx={{ margin: "10px 0 0 12px", gap: "6px", width: "100%" }}
      >
        <FormControlLabelStyled
          value={AlertLevel.ADVISORY}
          control={<Radio />}
          label={<AlertPillHierarchy alert_level={AlertLevel.ADVISORY} />}
        />
        <FormControlLabelStyled
          value={AlertLevel.WATCH}
          control={<Radio />}
          label={<AlertPillHierarchy alert_level={AlertLevel.WATCH} />}
        />
        <FormControlLabelStyled
          value={AlertLevel.WARNING}
          control={<Radio />}
          label={<AlertPillHierarchy alert_level={AlertLevel.WARNING} />}
        />
      </RadioGroup>
    </FormControl>
  )
}

const AlertPillHierarchy: React.FC<{ alert_level: AlertLevel }> = ({
  alert_level,
}) => {
  switch (alert_level) {
    case AlertLevel.ADVISORY:
      return (
        <AlertPillRow>
          <AlertPill alert_level={AlertLevel.ADVISORY} />
          <AlertPill alert_level={AlertLevel.WATCH} />
          <AlertPill alert_level={AlertLevel.WARNING} />
        </AlertPillRow>
      )
    case AlertLevel.WATCH:
      return (
        <AlertPillRow>
          <AlertPill alert_level={AlertLevel.WATCH} />
          <AlertPill alert_level={AlertLevel.WARNING} />
        </AlertPillRow>
      )
    case AlertLevel.WARNING:
      return (
        <AlertPillRow>
          <AlertPill alert_level={AlertLevel.WARNING} />
        </AlertPillRow>
      )
  }
}
